/**
 * Tool detail page — shared between v1 and v2.
 *
 * Tools track condition + asset/model/serial numbers. The free-form
 * `type` and `location` fields from the v1 page have no real D1 columns
 * (see workers/api/routes/entities.ts PATCH_ALIASES) so they're omitted
 * from the editable surface.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Edit2, Trash2, Wrench } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/PageHeader";
import { EntityImageGallery } from "@/components/EntityImageGallery";
import { EditEntityDialog } from "@/components/v2/dialogs/EditEntityDialog";
import { DeleteEntityDialog } from "@/components/v2/dialogs/DeleteEntityDialog";
import type { FieldDef } from "@/components/v2/dialogs/AddEntityDialog";
import { authFetch } from "@/lib/auth";

type ConditionStatus = "serviceable" | "needs_repair" | "out_of_service" | "retired";

interface Tool {
  id: string;
  ranch_id: string;
  name: string;
  asset_tag: string | null;
  model_number: string | null;
  serial_number: string | null;
  condition_status: ConditionStatus | null;
  acquisition_date: string | null;
  storage_location_id: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

const STATUS_LABEL: Record<ConditionStatus, string> = {
  serviceable: "Serviceable",
  needs_repair: "Needs Repair",
  out_of_service: "Out of Service",
  retired: "Retired",
};
const STATUS_TONE: Record<ConditionStatus, string> = {
  serviceable: "bg-moss/10 text-moss dark:bg-moss-dark/20 dark:text-moss-dark",
  needs_repair: "bg-clay/10 text-clay dark:bg-clay-dark/20 dark:text-clay-dark",
  out_of_service: "bg-charcoal/10 text-charcoal dark:bg-charcoal-dark/20 dark:text-charcoal-dark",
  retired: "bg-charcoal/10 text-charcoal dark:bg-charcoal-dark/20 dark:text-charcoal-dark",
};

const FIELDS: FieldDef[] = [
  { name: "name", label: "Name", required: true, placeholder: "Cordless drill" },
  { name: "asset_tag", label: "Asset tag", half: true, placeholder: "TOOL-001" },
  {
    name: "condition_status",
    label: "Condition",
    type: "select",
    half: true,
    options: (Object.keys(STATUS_LABEL) as ConditionStatus[]).map((v) => ({
      value: v,
      label: STATUS_LABEL[v],
    })),
  },
  { name: "model_number", label: "Model #", half: true },
  { name: "serial_number", label: "Serial #", half: true },
  { name: "notes", label: "Notes", type: "textarea" },
];

function transformTool(v: Record<string, string>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (v.name !== undefined) out.name = v.name;
  if (v.asset_tag !== undefined) out.asset_tag = v.asset_tag || null;
  if (v.condition_status !== undefined)
    out.condition_status = v.condition_status || "serviceable";
  if (v.model_number !== undefined) out.model_number = v.model_number || null;
  if (v.serial_number !== undefined) out.serial_number = v.serial_number || null;
  if (v.notes !== undefined) out.notes = v.notes || null;
  return out;
}

function statusOf(t: Tool): ConditionStatus {
  return t.condition_status ?? "serviceable";
}

export function ToolDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [tool, setTool] = useState<Tool | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  const load = useCallback(async (): Promise<void> => {
    if (!id) return;
    try {
      const data = await authFetch<Tool>(`/api/tools/${id}`);
      setTool(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load tool");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load, refreshTick]);

  if (loading) {
    return (
      <>
        <PageHeader
          title=""
          breadcrumbs={[
            { label: "Dashboard", href: "/" },
            { label: "Tools", href: "/tools" },
            { label: "Loading…" },
          ]}
        />
        <Skeleton className="h-48 w-full rounded-lg" />
      </>
    );
  }

  if (error || !tool) {
    return (
      <>
        <PageHeader
          title="Tool Not Found"
          breadcrumbs={[
            { label: "Dashboard", href: "/" },
            { label: "Tools", href: "/tools" },
            { label: "Error" },
          ]}
        />
        <div className="rounded-lg bg-destructive/10 p-4 text-sm text-destructive">
          {error || "Tool not found"}
        </div>
      </>
    );
  }

  const status = statusOf(tool);

  return (
    <>
      <PageHeader
        title={tool.name}
        breadcrumbs={[
          { label: "Dashboard", href: "/" },
          { label: "Tools", href: "/tools" },
          { label: tool.name },
        ]}
      >
        <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
          <Edit2 className="mr-1 h-4 w-4" /> Edit
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setDeleteOpen(true)}
          className="text-destructive border-destructive/30"
        >
          <Trash2 className="mr-1 h-4 w-4" /> Archive
        </Button>
      </PageHeader>

      <div className="space-y-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2">
              <Wrench className="h-5 w-5 text-ranch-brown" /> Tool Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Condition</p>
                <p className="mt-1">
                  <span
                    className={`inline-flex rounded-full px-2 py-0.5 font-mono text-2xs uppercase tracking-wider ${STATUS_TONE[status]}`}
                  >
                    {STATUS_LABEL[status]}
                  </span>
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Asset tag</p>
                <p className="font-medium font-mono text-sm">{tool.asset_tag ?? "—"}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Model #</p>
                <p className="font-medium">{tool.model_number ?? "—"}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Serial #</p>
                <p className="font-medium font-mono text-sm">{tool.serial_number ?? "—"}</p>
              </div>
              {tool.notes ? (
                <div className="col-span-2">
                  <p className="text-sm text-muted-foreground">Notes</p>
                  <p className="text-sm whitespace-pre-wrap">{tool.notes}</p>
                </div>
              ) : null}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <EntityImageGallery entityType="tool" entityId={tool.id} />
          </CardContent>
        </Card>
      </div>

      <EditEntityDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        title={`Edit ${tool.name}`}
        endpoint="/api/tools"
        entityId={tool.id}
        fields={FIELDS}
        initialValues={{
          name: tool.name,
          asset_tag: tool.asset_tag ?? "",
          condition_status: status,
          model_number: tool.model_number ?? "",
          serial_number: tool.serial_number ?? "",
          notes: tool.notes ?? "",
        }}
        transform={transformTool}
        onSaved={() => setRefreshTick((t) => t + 1)}
      />
      <DeleteEntityDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        endpoint="/api/tools"
        entityId={tool.id}
        entityName={tool.name}
        onDeleted={() => navigate("/tools")}
      />
    </>
  );
}
